import React, { useEffect, useState } from 'react';
import { ResponsiveObject } from '@tq/ui-components/dist/styled-system/utils';
import { Box, Flex, Link, useBreakpointValue, useThemedValueSwitch } from '@tq/ui-components';
import { useTranslation } from 'react-i18next';

export type CardExpandableParagraphProps = {
  maxCharacters: ResponsiveObject<number>;
  showMoreText?: string;
  showLessText?: string;
  showButton?: boolean;
  expanded?: boolean;
  onExpanded?: () => void;
};
export const ExpandableParagraph: React.FC<CardExpandableParagraphProps> = ({
  maxCharacters,
  children,
  showLessText,
  showMoreText,
  showButton = true,
  expanded = false,
  onExpanded,
}) => {
  const { tvs } = useThemedValueSwitch();
  const [originalText, setOriginalText] = useState(children?.toString());
  const [truncatedText, setTruncatedText] = useState(originalText);
  const [isExpanded, setIsExpanded] = useState(expanded);

  const { t } = useTranslation('card_expandable_paragraph');
  const maxCharactersBeforeTruncation = useBreakpointValue(maxCharacters) || 50;

  const text = isExpanded ? originalText : truncatedText + '...';
  const showMore = showMoreText ?? t('show_more', 'Show more');
  const showLess = showLessText ?? t('show_less', 'Show less');
  const buttonText = isExpanded ? showLess : showMore;
  const originalTextLength = originalText?.length || 0;

  const handleExpandedChanged = () => {
    setIsExpanded((prevIsExpanded) => !prevIsExpanded);
    onExpanded?.();
  };

  useEffect(() => {
    const newText = children?.toString();
    setOriginalText(newText);
    setTruncatedText(newText?.slice(0, maxCharactersBeforeTruncation));
    setIsExpanded(expanded);
  }, [children, maxCharactersBeforeTruncation, expanded]);

  const renderText = (textToRender?: string) => (
    <Box maxWidth='85%' whiteSpace='revert' fontSize='sm'>
      {textToRender}
    </Box>
  );

  if (originalTextLength <= maxCharactersBeforeTruncation) {
    return renderText(originalText);
  }

  return (
    <Flex
      justifyContent='space-between'
      alignItems={{ base: 'unset', lg: 'center' }}
      flexDirection={{ base: 'column', lg: 'row' }}
    >
      {renderText(text)}
      {showButton && (
        <Link
          onClick={(e) => {
            e.stopPropagation();
            handleExpandedChanged();
          }}
          textAlign={{ base: 'center', lg: 'initial' }}
          fontSize={tvs({ tq: '.875rem' }, '.75rem')}
          my={{ base: 4, lg: 0 }}
        >
          {buttonText}
        </Link>
      )}
    </Flex>
  );
};
